import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';

import { getMealStatistics, getMealCostReport } from '../../services/mealEntryService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import DateTimePicker from '../../components/ui/DateTimePicker';
import PersonCell from '../../components/ui/PersonCell';

const Wrapper = styled.div``;

const Filters = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 12px;
  align-items: end;
  background: #fff;
  padding: 16px;
  border-radius: 6px;
  margin: 16px 0;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const KpiCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const KpiLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px; color: #64748b;
  text-transform: none;
`;

const KpiValue = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 24px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 6px;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const CardTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 12px;
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse;
  th, td {
    text-align: left; padding: 8px 6px;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;
    border-bottom: 1px solid #f1f5f9;
  }
  th { font-weight: 600; color: #64748b; font-size: 12px; }
  td.num { text-align: right; }
`;

const Bar = styled.div`
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(90deg, #0291EF, #02D3BF);
  width: ${({ $pct }) => `${$pct}%`};
  margin-top: 4px;
`;

const ReportCardHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 12px;
`;

function defaultRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  return { from: toIso(first), to: toIso(today) };
}

export default function MealStatisticsPage() {
  const { t } = useTranslation();

  const [range, setRange] = useState(defaultRange());
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: range.from, to: range.to };
      const [s, r] = await Promise.all([
        getMealStatistics(params),
        getMealCostReport(params),
      ]);
      setStats(s.data?.data || null);
      const rep = r.data?.data;
      setReport(Array.isArray(rep) ? rep : []);
    } catch { /* interceptor */ }
    setLoading(false);
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const denialRate = useMemo(() => {
    if (!stats) return 0;
    const denials = (stats.denialBreakdown || []).reduce((s, x) => s + x.count, 0);
    const total = stats.totalMeals + denials;
    return total ? Math.round((denials / total) * 100) : 0;
  }, [stats]);

  const avgDaily = useMemo(() => {
    if (!stats || !stats.byDate?.length) return 0;
    const days = Math.max(1, stats.byDate.length);
    return Math.round(stats.totalMeals / days);
  }, [stats]);

  const handleExportCsv = useCallback(() => {
    if (!report.length) return;
    const header = [
      t('canteen.user'),
      t('canteen.registrationNumber'),
      t('canteen.department'),
      t('canteen.totalMeals'),
      t('canteen.totalCost'),
      t('canteen.currency'),
    ];
    const rows = report.map((r) => [
      r.fullName || '',
      r.registrationNumber || '',
      r.departmentName || '',
      r.totalMeals,
      r.totalCost,
      r.currency || '',
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((c) => {
        const v = String(c ?? '').replace(/"/g, '""');
        return /[,\n"]/.test(v) ? `"${v}"` : v;
      }).join(','))
      .join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meal-cost-report_${range.from}_${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [report, range, t]);

  const maxByMeal = Math.max(1, ...(stats?.byMeal || []).map((x) => x.count));
  const maxByUser = Math.max(1, ...(stats?.topUsers || []).map((x) => x.count));

  return (
    <Wrapper>
      <Breadcrumbs items={[{ label: t('canteen.statistics') }]} />

      <Filters>
        <DateTimePicker
          label={t('canteen.fromDate')}
          pickerType="date"
          value={range.from}
          onChange={(v) => setRange((p) => ({ ...p, from: v }))}
        />
        <DateTimePicker
          label={t('canteen.toDate')}
          pickerType="date"
          value={range.to}
          onChange={(v) => setRange((p) => ({ ...p, to: v }))}
        />
        <Button onClick={load} disabled={loading}>{loading ? '...' : t('common.filter', { defaultValue: 'Filtrele' })}</Button>
      </Filters>

      <KpiGrid>
        <KpiCard>
          <KpiLabel>{t('canteen.totalMeals')}</KpiLabel>
          <KpiValue>{stats?.totalMeals ?? 0}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>{t('canteen.totalCost')}</KpiLabel>
          <KpiValue>{formatCost(stats?.totalCost, stats?.currency)}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>{t('canteen.averageDailyMeals')}</KpiLabel>
          <KpiValue>{avgDaily}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>{t('canteen.denialRate')}</KpiLabel>
          <KpiValue>{denialRate}%</KpiValue>
        </KpiCard>
      </KpiGrid>

      <Grid2>
        <Card>
          <CardTitle>{t('canteen.byMeal')}</CardTitle>
          <Table>
            <thead>
              <tr>
                <th>{t('canteen.meal')}</th>
                <th className="num">{t('canteen.totalMeals')}</th>
                <th className="num">{t('canteen.totalCost')}</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.byMeal || []).map((m) => (
                <tr key={m.mealId}>
                  <td>
                    {m.mealName}
                    <Bar $pct={(m.count / maxByMeal) * 100} />
                  </td>
                  <td className="num">{m.count}</td>
                  <td className="num">{formatCost(m.cost, stats?.currency)}</td>
                </tr>
              ))}
              {!(stats?.byMeal?.length) && (
                <tr><td colSpan={3} style={{ color: '#A0A8B4', textAlign: 'center' }}>{t('common.noRecords')}</td></tr>
              )}
            </tbody>
          </Table>
        </Card>

        <Card>
          <CardTitle>{t('canteen.topUsers')}</CardTitle>
          <Table>
            <thead>
              <tr>
                <th>{t('canteen.user')}</th>
                <th className="num">{t('canteen.totalMeals')}</th>
                <th className="num">{t('canteen.totalCost')}</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.topUsers || []).map((u) => (
                <tr key={u.userId}>
                  <td>
                    {u.userName}
                    <Bar $pct={(u.count / maxByUser) * 100} />
                  </td>
                  <td className="num">{u.count}</td>
                  <td className="num">{formatCost(u.cost, stats?.currency)}</td>
                </tr>
              ))}
              {!(stats?.topUsers?.length) && (
                <tr><td colSpan={3} style={{ color: '#A0A8B4', textAlign: 'center' }}>{t('common.noRecords')}</td></tr>
              )}
            </tbody>
          </Table>
        </Card>
      </Grid2>

      <Grid2>
        <Card>
          <CardTitle>{t('canteen.byDate')}</CardTitle>
          <Table>
            <thead>
              <tr>
                <th>{t('canteen.menuDate')}</th>
                <th className="num">{t('canteen.totalMeals')}</th>
                <th className="num">{t('canteen.totalCost')}</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.byDate || []).map((d) => (
                <tr key={d.date}>
                  <td>{d.date}</td>
                  <td className="num">{d.count}</td>
                  <td className="num">{formatCost(d.cost, stats?.currency)}</td>
                </tr>
              ))}
              {!(stats?.byDate?.length) && (
                <tr><td colSpan={3} style={{ color: '#A0A8B4', textAlign: 'center' }}>{t('common.noRecords')}</td></tr>
              )}
            </tbody>
          </Table>
        </Card>

        <Card>
          <CardTitle>{t('canteen.denialBreakdown')}</CardTitle>
          <Table>
            <thead>
              <tr>
                <th>{t('canteen.denyReason')}</th>
                <th className="num">{t('canteen.totalMeals')}</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.denialBreakdown || []).map((d) => (
                <tr key={d.reason}>
                  <td>{t(`responseErrors.${d.reason}`, { defaultValue: d.reason })}</td>
                  <td className="num">{d.count}</td>
                </tr>
              ))}
              {!(stats?.denialBreakdown?.length) && (
                <tr><td colSpan={2} style={{ color: '#A0A8B4', textAlign: 'center' }}>{t('common.noRecords')}</td></tr>
              )}
            </tbody>
          </Table>
        </Card>
      </Grid2>

      <Card>
        <ReportCardHeader>
          <CardTitle style={{ margin: 0 }}>{t('canteen.costReport')}</CardTitle>
          <Button variant="ghost" onClick={handleExportCsv} disabled={!report.length}>
            <Download size={14} /> {t('canteen.exportExcel')}
          </Button>
        </ReportCardHeader>
        <Table>
          <thead>
            <tr>
              <th>{t('canteen.user')}</th>
              <th>{t('canteen.registrationNumber')}</th>
              <th>{t('canteen.department')}</th>
              <th className="num">{t('canteen.totalMeals')}</th>
              <th className="num">{t('canteen.totalCost')}</th>
            </tr>
          </thead>
          <tbody>
            {report.map((r) => (
              <tr key={r.userId}>
                <td><PersonCell name={r.fullName} profilePicture={r.profilePicture} size={26} /></td>
                <td>{r.registrationNumber || '-'}</td>
                <td>{r.departmentName || '-'}</td>
                <td className="num">{r.totalMeals}</td>
                <td className="num">{formatCost(r.totalCost, r.currency)}</td>
              </tr>
            ))}
            {!report.length && (
              <tr><td colSpan={5} style={{ color: '#A0A8B4', textAlign: 'center' }}>{t('common.noRecords')}</td></tr>
            )}
          </tbody>
        </Table>
      </Card>
    </Wrapper>
  );
}

function formatCost(v, currency) {
  if (v == null) return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${currency ? ' ' + currency : ''}`;
}
